import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
    const { t } = useLanguage();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error(t("common.passwordsDoNotMatch"));
            return;
        }
        if (!token) {
            toast.error(t("reset.missingToken"));
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success(t("reset.success"));
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-8 relative">
            <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
            <div className="max-w-md w-full space-y-6" data-testid="reset-password-screen">
                <div>
                    <div className="label-mono mb-3">{t("reset.label")}</div>
                    <h1 className="font-heading text-3xl font-semibold">{t("reset.title")}</h1>
                    <p className="text-muted-foreground mt-2">{t("reset.subtitle")}</p>
                </div>
                <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                    <div className="space-y-2">
                        <Label htmlFor="password">{t("reset.newPassword")}</Label>
                        <Input id="password" type="password" required minLength={6} value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 rounded-md" data-testid="reset-password-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="confirm">{t("reset.confirm")}</Label>
                        <Input id="confirm" type="password" required minLength={6} value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="h-12 rounded-md" data-testid="reset-confirm-input" />
                    </div>
                    <Button type="submit" disabled={loading}
                        className="w-full h-12 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="reset-submit-button">
                        {loading ? t("reset.submitting") : t("reset.submit")}
                    </Button>
                </form>
                <div className="text-sm text-muted-foreground text-center">
                    <Link to="/login" className="hover:text-primary" data-testid="reset-login-link">{t("reset.backToLogin")}</Link>
                </div>
            </div>
        </div>
    );
}
